import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { down } from '@/assets/icons';
import { AsaphDropdownMenu } from '@/components/shared/AsaphDropdownMenu';
import { ModalSectionCard } from '@/components/shared/ModalSectionCard';
import { ModalSectionTitle } from '@/components/shared/ModalSectionTitle';
import { RoundedButton } from '@/components/shared/RoundedButton';
import { getSongThemeColor } from '@/components/shared/SongThemeChip';
import {
	WordCloudChart,
	WordCloudChartDataItem,
} from '@/components/shared/WordCloudChart/WordCloudChart';
import { CHART_PALETTE } from '@/helpers/chartPalette';
import { ChartSectionData } from '@/helpers/reportsDataHelpers';
import { TagDistributionPoint, TagTrendPoint } from '@/hooks/useReportsData';
import { SvgIcon } from '@material-ui/core';
import { ResponsiveCirclePackingCanvas } from '@nivo/circle-packing';

import { useAnalyticsEvent } from '@/providers/AnalyticsProvider';

import { ChartEmptyState } from '../ChartEmptyState/ChartEmptyState';
import styles from './DistributionCirclePackingChart.module.scss';

type CirclePackingLeaf = {
	name: string;
	label?: string;
	date?: string;
	count?: number;
};
type CirclePackingNode = {
	name: string;
	count?: number;
	children?: CirclePackingLeaf[];
};

type Props =
	| {
			title: string;
			section: ChartSectionData<TagDistributionPoint>;
			perSetlist?: false;
			semanticColors?: boolean;
			defaultView?: 'bubble' | 'word-cloud' | 'rank';
			singleSource?: boolean;
			sharedLabels?: string[];
	  }
	| {
			title: string;
			section: ChartSectionData<TagTrendPoint>;
			perSetlist: true;
			semanticColors?: boolean;
			defaultView?: 'bubble' | 'word-cloud' | 'rank';
			singleSource?: boolean;
			sharedLabels?: string[];
	  };

const FALLBACK = '#d6d6e7';

const EMOTION_WORD_CLOUD_COLORS: Record<string, { bg: string; text: string }> =
	{
		joy: { bg: '#fdf98b', text: '#a06f08' },
		trust: { bg: '#e1ff95', text: '#548605' },
		fear: { bg: '#b2ffc5', text: '#05902b' },
		surprise: { bg: '#a3fefe', text: '#0b8593' },
		sadness: { bg: '#e3dcf8', text: '#633fb1' },
		disgust: { bg: '#fccfd6', text: '#b41743' },
		anger: { bg: '#ffcdc5', text: '#c9270c' },
		anticipation: { bg: '#fbddad', text: '#bc5012' },
	};

// Blue scale 100–700: index 0 = lightest (smallest circles), index 6 = darkest (largest circles)
const BLUE_SCALE = [
	'#d5efff', // 100
	'#b3dfff', // 200
	'#85c7ff', // 300
	'#56a0ff', // 400
	'#2f79ff', // 500
	'#0c4bff', // 600
	'#0040ff', // 700
];

const VIEW_OPTIONS: {
	label: string;
	value: 'bubble' | 'word-cloud' | 'rank';
}[] = [
	{ label: 'Bubble', value: 'bubble' },
	{ label: 'Word Cloud', value: 'word-cloud' },
	{ label: 'Rank', value: 'rank' },
];

function toTitleCase(s: string): string {
	return s.replace(/\b(?<!')\w/g, (c) => c.toUpperCase());
}

function hexToRgba(hex: string, alpha: number): string {
	const r = parseInt(hex.slice(1, 3), 16);
	const g = parseInt(hex.slice(3, 5), 16);
	const b = parseInt(hex.slice(5, 7), 16);
	return `rgba(${r},${g},${b},${alpha})`;
}

export const DistributionCirclePackingChart: React.FC<Props> = ({
	title,
	section,
	perSetlist,
	semanticColors = false,
	defaultView = 'bubble',
	singleSource = false,
	sharedLabels,
}) => {
	const [viewMode, setViewMode] = useState<'bubble' | 'word-cloud' | 'rank'>(
		defaultView,
	);
	const [anchorEl, setAnchorEl] = useState<Element | null>(null);
	const [hoveredLabel, setHoveredLabel] = useState<string | null>(null);
	const fireViewModeChanged = useAnalyticsEvent('Distribution_Chart_View_Mode_Changed');
	const [dimAlpha, setDimAlpha] = useState(1);
	const lastTooltipLabel = useRef<string | null>(null);
	const tooltipFiredRef = useRef(false);
	const animFrameRef = useRef<number | null>(null);
	const currentAlphaRef = useRef(1);

	const animateTo = useCallback((target: number, onDone?: () => void) => {
		if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
		const tick = () => {
			const diff = target - currentAlphaRef.current;
			if (Math.abs(diff) < 0.015) {
				currentAlphaRef.current = target;
				setDimAlpha(target);
				onDone?.();
				return;
			}
			currentAlphaRef.current += diff * 0.15;
			setDimAlpha(currentAlphaRef.current);
			animFrameRef.current = requestAnimationFrame(tick);
		};
		animFrameRef.current = requestAnimationFrame(tick);
	}, []);

	useEffect(
		() => () => {
			if (animFrameRef.current)
				cancelAnimationFrame(animFrameRef.current);
		},
		[],
	);

	// Derive the ordered unique label list from whichever data shape we have.
	const labels = useMemo<string[]>(() => {
		if (perSetlist) {
			const seen = new Set<string>();
			const result: string[] = [];
			for (const p of section.data as TagTrendPoint[]) {
				if (!seen.has(p.label)) {
					seen.add(p.label);
					result.push(p.label);
				}
			}
			return result;
		}
		return (section.data as TagDistributionPoint[]).map((d) => d.label);
	}, [section.data, perSetlist]);

	// Grand total of all counts — every bubble's % is relative to this,
	// so the whole chart sums to 100%.
	const grandTotal = useMemo<number>(() => {
		if (perSetlist) {
			return (section.data as TagTrendPoint[]).reduce(
				(sum, p) => sum + p.count,
				0,
			);
		}
		return (section.data as TagDistributionPoint[]).reduce(
			(sum, d) => sum + d.count,
			0,
		);
	}, [section.data, perSetlist]);

	const colorMap = useMemo(() => {
		const map = new Map<string, string>();

		if (semanticColors) {
			let paletteIdx = 0;
			labels.forEach((label) => {
				const { colorHex } = getSongThemeColor(label);
				map.set(
					label,
					colorHex !== FALLBACK
						? colorHex
						: CHART_PALETTE[paletteIdx++ % CHART_PALETTE.length],
				);
			});
			return map;
		}

		// Blue scale: sum counts per label, normalize within the dataset range,
		// map to 9 stops where largest → 900 (darkest) and smallest → 100 (lightest)
		const totals = new Map<string, number>();
		if (perSetlist) {
			for (const p of section.data as TagTrendPoint[]) {
				totals.set(p.label, (totals.get(p.label) ?? 0) + p.count);
			}
		} else {
			for (const d of section.data as TagDistributionPoint[]) {
				totals.set(d.label, d.count);
			}
		}

		const values = labels.map((l) => totals.get(l) ?? 0);
		const min = Math.min(...values);
		const max = Math.max(...values);

		labels.forEach((label) => {
			const count = totals.get(label) ?? 0;
			const shadeIndex =
				count >= max
					? 6
					: Math.round(((count - min) / (max - min)) * 6);
			map.set(label, BLUE_SCALE[shadeIndex]);
		});

		return map;
	}, [labels, semanticColors, section.data, perSetlist]);

	const bubbleColors = useCallback(
		(node: { data: unknown }) => {
			const d = node.data as CirclePackingLeaf;
			const label = d.label ?? d.name;
			const hex = colorMap.get(label) ?? CHART_PALETTE[0];
			if (!hoveredLabel || hoveredLabel === label) return hex;
			return hexToRgba(hex, dimAlpha);
		},
		[colorMap, hoveredLabel, dimAlpha],
	);

	const nivoData: CirclePackingNode = useMemo(() => {
		if (perSetlist) {
			return {
				name: 'root',
				children: (section.data as TagTrendPoint[]).map((p) => ({
					name: `${p.setlistId}::${p.label}`,
					label: p.label,
					date: p.date,
					count: p.count,
				})),
			};
		}
		return {
			name: 'root',
			children: (section.data as TagDistributionPoint[]).map((d) => ({
				name: d.label,
				count: d.count,
			})),
		};
	}, [section.data, perSetlist]);

	const wordCloudData = useMemo<WordCloudChartDataItem[]>(() => {
		if (perSetlist) {
			const totals = new Map<string, number>();
			for (const p of section.data as TagTrendPoint[]) {
				totals.set(p.label, (totals.get(p.label) ?? 0) + p.count);
			}
			return Array.from(totals.entries()).map(([label, value]) => ({
				label,
				value,
			}));
		}
		return (section.data as TagDistributionPoint[]).map((d) => ({
			label: d.label,
			value: d.count,
		}));
	}, [section.data, perSetlist]);

	const top10Data = useMemo(
		() => [...wordCloudData].sort((a, b) => b.value - a.value).slice(0, 10),
		[wordCloudData],
	);

	const listAll = wordCloudData;
	const avgValue =
		wordCloudData.length > 0
			? wordCloudData.reduce((s, i) => s + i.value, 0) /
			  wordCloudData.length
			: 0;
	const listMulti = singleSource
		? []
		: sharedLabels
		? wordCloudData.filter((item) => sharedLabels.includes(item.label))
		: wordCloudData.filter((item) => item.value > avgValue);

	const chevron = <SvgIcon component={down} style={{ fontSize: 14 }} />;

	const toggleChip = (
		<>
			<RoundedButton
				size="md"
				trailingComponent={chevron}
				onClick={(e) => setAnchorEl(e.currentTarget)}
			>
				{VIEW_OPTIONS.find((o) => o.value === viewMode)?.label ??
					'Bubble'}
			</RoundedButton>
			<AsaphDropdownMenu
				open={!!anchorEl}
				anchorEl={anchorEl}
				onClose={() => setAnchorEl(null)}
				selectable
				items={VIEW_OPTIONS.map((opt) => ({
					label: opt.label,
					selected: viewMode === opt.value,
					onClick: () => {
						fireViewModeChanged({ mode: opt.value });
						setViewMode(opt.value);
						setAnchorEl(null);
					},
				}))}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
			/>
		</>
	);

	if (section.emptyReason !== null || section.data.length === 0) {
		return (
			<ModalSectionCard paddingSize="lg">
				<ModalSectionTitle>{title}</ModalSectionTitle>
				<ChartEmptyState copy="Complete a setlist to see this chart." />
			</ModalSectionCard>
		);
	}


	return (
		<ModalSectionCard paddingSize="lg" className={styles['card']}>
			<ModalSectionTitle sideComponent={toggleChip}>
				{title}
			</ModalSectionTitle>
			{viewMode === 'bubble' && (
				<>
					<div className={styles['chart']}>
					<div
						className={styles['chart-canvas']}
						onMouseMove={() => {
							tooltipFiredRef.current = false;
							requestAnimationFrame(() => {
								if (
									!tooltipFiredRef.current &&
									lastTooltipLabel.current !== null
								) {
									lastTooltipLabel.current = null;
									animateTo(1, () => setHoveredLabel(null));
								}
							});
						}}
						onMouseLeave={() => {
							lastTooltipLabel.current = null;
							animateTo(1, () => setHoveredLabel(null));
						}}
					>
						<ResponsiveCirclePackingCanvas
							data={nivoData}
							id="name"
							value="count"
							leavesOnly={true}
							colorBy="id"
							colors={bubbleColors}
							enableLabels={false}
							isInteractive={true}
							padding={4}
							animate={false}
							borderWidth={0}
							tooltip={(datum) => {
								tooltipFiredRef.current = true;
								const d = datum.data as CirclePackingLeaf;
								const label = d.label ?? String(datum.id);
								if (lastTooltipLabel.current !== label) {
									lastTooltipLabel.current = label;
									requestAnimationFrame(() =>
										setHoveredLabel(label),
									);
									animateTo(0.15);
								}
								const date = d.date
									? perSetlist
										? d.date
										: new Date(
												d.date + 'T00:00:00',
										  ).toLocaleDateString('en-US', {
												month: 'short',
												day: 'numeric',
										  })
									: null;
								const pct =
									grandTotal > 0
										? (
												(datum.value / grandTotal) *
												100
										  ).toFixed(1)
										: '0.0';
								return (
									<div
										style={{
											background: 'white',
											padding: '8px 12px',
											borderRadius: 4,
											boxShadow:
												'0 2px 8px rgba(0,0,0,.12)',
											lineHeight: 1.4,
											textAlign: 'center',
											fontSize: 12,
										}}
									>
										<div style={{ fontWeight: 500 }}>
											{toTitleCase(label)}
											{' · '}
											{pct}%
										</div>
										{date && (
											<div
												style={{
													color: '#6b7280',
													marginTop: 2,
												}}
											>
												{date}
											</div>
										)}
									</div>
								);
							}}
							theme={{
								textColor: '#23263b',
								tooltip: { container: { background: '#fff' } },
							}}
						/>
					</div>
					</div>
					<div className={styles['legend']}>
						{top10Data.map(({ label }) => (
							<div
								key={label}
								className={styles['legend-item']}
								data-active={hoveredLabel === label}
								style={{
									opacity:
										hoveredLabel && hoveredLabel !== label
											? 0.3
											: 1,
									cursor: 'default',
								}}
								onMouseEnter={() => {
									setHoveredLabel(label);
									animateTo(0.15);
								}}
								onMouseLeave={() =>
									animateTo(1, () => setHoveredLabel(null))
								}
							>
								<div
									className={styles['legend-circle']}
									style={{
										background:
											colorMap.get(label) ??
											CHART_PALETTE[0],
									}}
								/>
								<span className={styles['legend-label']}>
									{toTitleCase(label)}
								</span>
							</div>
						))}
					</div>
				</>
			)}
			{viewMode === 'word-cloud' &&
				(wordCloudData.length === 0 ? (
					<ChartEmptyState copy="Not enough data to show a word cloud." />
				) : (
					<WordCloudChart
						sourceLists={[listAll, listMulti]}
						reduced={false}
						hideStatus={singleSource}
						labels={
							semanticColors || singleSource
								? undefined
								: ['Unique', 'Shared']
						}
						classOverrides={
							semanticColors
								? Object.fromEntries(
										wordCloudData
											.filter(
												(d) =>
													getSongThemeColor(d.label)
														.className !==
													'px-color-scheme-grey',
											)
											.map((d) => [
												d.label,
												getSongThemeColor(d.label)
													.className,
											]),
								  )
								: undefined
						}
						colorOverrides={
							semanticColors
								? Object.fromEntries(
										wordCloudData
											.filter((d) => {
												const notInSongTheme =
													getSongThemeColor(d.label)
														.className ===
													'px-color-scheme-grey';
												return (
													notInSongTheme &&
													!!EMOTION_WORD_CLOUD_COLORS[
														d.label.toLowerCase()
													]
												);
											})
											.map((d) => [
												d.label,
												EMOTION_WORD_CLOUD_COLORS[
													d.label.toLowerCase()
												],
											]),
								  )
								: undefined
						}
						className={styles['word-cloud']}
					/>
				))}
			{viewMode === 'rank' &&
				(top10Data.length === 0 ? (
					<ChartEmptyState copy="Not enough data." />
				) : (
					<div className={styles['top-10']}>
						{top10Data.map((item) => {
							const pct =
								top10Data[0].value > 0
									? (item.value / top10Data[0].value) * 100
									: 0;
							const schemeClass = semanticColors
								? getSongThemeColor(item.label).className
								: undefined;
							return (
								<div
									key={item.label}
									className={styles['top-10-row']}
								>
									<div
										className={`${styles['top-10-track']}${
											schemeClass ? ` ${schemeClass}` : ''
										}`}
									>
										<div
											className={styles['top-10-bar']}
											style={{
												width: `${pct}%`,
												...(schemeClass && {
													background:
														'var(--px-color-scheme-200)',
												}),
											}}
										/>
										<span
											className={styles['top-10-label']}
											style={
												schemeClass
													? {
															color: 'var(--px-color-scheme-contrast)',
													  }
													: undefined
											}
										>
											{toTitleCase(item.label)}
										</span>
									</div>
									{/* <span className={styles['top-10-count']}>{item.value}</span> */}
								</div>
							);
						})}
					</div>
				))}
		</ModalSectionCard>
	);
};
